import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IonContent, IonPage, useIonViewDidLeave, useIonViewWillEnter } from '@ionic/react';
import { Container, CustomButton, SpecialistCard, SupportedCurrency } from '@mindly/ui-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllSelectedAnswers,
  getOnBoardingID,
  getOnBoardingName,
  getRegisterUserID,
  getStepNumber,
  getVars,
} from '../../../../redux/onBoarding/selector';
import { SkeletonPsychologistList } from '../../../../components/SkeletonPsychologistList';
import useLangs from '../../../../context/LangsContext';
import { specialistsSelector } from '../../../../redux/specialists/selector';
import { globalVariables } from '../../../../redux/firestoreGlobalVariables/selector';
import { FormattedMessage, useIntl } from 'react-intl';
import SpecialistProfileVideoModal from '../../../SpecialistProfile/SpecialistProfileVideoModal/SpecialistProfileVideoModal';
import { OnBoardingSpecialistsWrapper } from './OnBoardingSpecialists.style';
import OnBoardingFooter from '../../components/OnBoardingFooter/OnBoardingFooter';
import { RouteComponentProps } from 'react-router-dom';
import { changeUserData } from '../../../../api/user';
import useOnBoardingNextStep from '../../../../hooks/useOnBoardingNextStep';
import useOnBoardingCurrentStep from '../../../../hooks/useOnBoardingCurrentStep';
import { OnBoardingTypes } from '../../../../redux/onBoarding/types';
import { ImpactStyle } from '@capacitor/haptics';
import { fetchSpecialistsList } from '../../../../redux/specialists/thunk';
import { SPECIALISTS_COUNT_PER_PAGE } from '../../../../redux/specialists/constants';
import { FunnelTypeEnum, useAnalytics } from 'src/shared';
import { setSplashColor } from 'src/shared/utils/splashColor';
import { useTranslation } from 'react-i18next';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';

interface OnBoardingSpecialistsProps extends RouteComponentProps<{ boardId: string; id: string }> {}

const OnBoardingSpecialists: FC<OnBoardingSpecialistsProps> = ({ match }) => {
  const { id } = match.params;
  const { locale } = useLangs();
  const dispatch = useDispatch();
  const intl = useIntl();
  const nextStepAction = useOnBoardingNextStep();
  const currentStepAction = useOnBoardingCurrentStep({
    ...match.params,
    type: OnBoardingTypes.SPECIALISTS_LIST,
  });
  const { i18n } = useTranslation();

  const selectedAnswers = useSelector(getAllSelectedAnswers);
  const registerUserID = useSelector(getRegisterUserID);
  const specialistsList = useSelector(specialistsSelector);
  const globalVars = useSelector(globalVariables);
  const onBoardingName = useSelector(getOnBoardingName);
  const stepNumber = useSelector(getStepNumber);
  const onBoardingVars = useSelector(getVars);
  const onBoardingId = useSelector(getOnBoardingID);

  const [isLoading, setIsLoading] = useState(false);
  const [videoURL, setVideoURL] = useState<string | null>(null);
  const [isPageView, setIsPageView] = useState(false);
  const [specCounter, setSpecCounter] = useState(0);
  const [isEventTracked, setIsEventTracked] = useState(false);

  const pageCounterRef = useRef(0);

  const { trackPredefinedEvent } = useAnalytics();

  useIonViewWillEnter(async () => {
    currentStepAction();

    setIsPageView(true);
    setIsEventTracked(false);

    setSplashColor('#fffffe');

    if (!registerUserID) return;
    if (!selectedAnswers) return;

    try {
      setIsLoading(true);
      await changeUserData(
        {
          onBoardingAnswers: selectedAnswers,
        },
        registerUserID
      );

      await dispatch(fetchSpecialistsList(locale));
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }, [currentStepAction, selectedAnswers, registerUserID]);

  useIonViewDidLeave(() => {
    setIsPageView(false);
    setIsEventTracked(false);
    pageCounterRef.current = 0;
  });

  useEffect(() => {
    if (!isPageView) return;
    if (pageCounterRef.current > 0) return;
    pageCounterRef.current += 1;
  }, [isPageView, stepNumber, onBoardingName, pageCounterRef]);

  const handleAnalyticEvent = () => {
    if (!onBoardingId) return;

    trackPredefinedEvent('FUNNEL_SCREEN_VIEWED', {
      funnel_id: onBoardingId,
      funnel_name: onBoardingName,
      funnel_type:
        onBoardingId === globalVars?.defaultOnBoardingId?.[i18n.language]
          ? FunnelTypeEnum.DEFAULT
          : FunnelTypeEnum.WEB_TO_APP,
      screen_id: id,
      screen_name: `Specialists screen: ${id}`,
      screen_order: stepNumber,
    });
    setIsEventTracked(true);
  };

  useEffect(() => {
    if (!isPageView) return;
    if (isLoading) return;
    if (!onBoardingId || !onBoardingName || !stepNumber) return;
    if (isEventTracked) return;

    handleAnalyticEvent();
  }, [isPageView, isLoading, onBoardingId, onBoardingName, stepNumber, isEventTracked]);

  const onSpecialistVideoClick = useCallback(
    (videoURLLocal: string | null) => () => {
      setVideoURL(videoURLLocal);
    },
    []
  );

  const onSpecialistVideoClose = useCallback(() => {
    setVideoURL(null);
  }, []);

  const handleNextStep = useCallback(async () => {
    await nextStepAction();
  }, [nextStepAction]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (onBoardingVars?.sps) {
      interval = setInterval(
        () => {
          setSpecCounter((prev) => (prev >= Number(onBoardingVars.sps) ? Number(onBoardingVars.sps) : prev + 1));
        },
        Math.ceil(3000 / Number(onBoardingVars.sps))
      );
    }
    return () => {
      clearInterval(interval);
    };
  }, [onBoardingVars.sps]);

  return (
    <IonPage>
      <IonContent className={'white'}>
        <Container style={{ paddingLeft: 0, paddingRight: 0 }}>
          {isLoading ? (
            <SkeletonPsychologistList psychologistCardSkeletonsToDraw={10} />
          ) : (
            <OnBoardingSpecialistsWrapper>
              <h2 className={'specialists-title'}>
                <FormattedMessage
                  id={'on-boarding.success.specialists.found'}
                  defaultMessage={'Психологів підібрано персонально для Вас'}
                />
                : {specCounter >= SPECIALISTS_COUNT_PER_PAGE ? `${specCounter}+` : specCounter}
              </h2>
              <div className={'specialists-list'}>
                {specialistsList?.map((specialist) => {
                  if (!specialist.is_active || (typeof specialist.is_public === 'boolean' && !specialist.is_public)) {
                    return null;
                  } else {
                    return (
                      <SpecialistCard
                        key={specialist.id}
                        avatarLink={specialist.specialist_list_photo_link}
                        sessionDurationInMinutes={specialist.session_duration_minutes ?? 50}
                        pricePerSession={specialist?.session_price ?? specialist?.session_price_uah}
                        currency={specialist?.session_price_type as unknown as SupportedCurrency}
                        specialization={specialist.specialization ?? ''}
                        fullName={`${specialist.first_name} ${specialist.last_name[0]}.`}
                        yearsOfExperience={specialist.years_of_experience}
                        summary={specialist.summary}
                        hoursOfPractice={specialist.hours_of_practice}
                        clientsCount={specialist?.active_clients}
                        sessionsCount={specialist?.sessions_count}
                        onFavouriteClickHandler={() => null}
                        isFavouriteSpecialist={false}
                        onCardClickHandler={handleNextStep}
                        countryCode={specialist?.country_of_birthday ?? 'ua'}
                        videoDurationSeconds={
                          specialist?.video_link ? specialist?.video_duration_seconds ?? 0 : undefined
                        }
                        onAvatarClickHandler={
                          specialist?.video_duration_seconds
                            ? onSpecialistVideoClick(specialist?.video_link ?? null)
                            : handleNextStep
                        }
                        percentMatch={specialist?.percent_match ?? undefined}
                        averageStarsCount={
                          specialist?.reviews_count >= globalVars.specialistReviewsCountForShowing
                            ? specialist?.reviews_average_rating
                            : undefined
                        }
                        countOfReviews={
                          specialist?.reviews_count >= globalVars.specialistReviewsCountForShowing
                            ? specialist?.reviews_count
                            : undefined
                        }
                        translations={{
                          name: intl.formatMessage({
                            id: 'specialist.card.name',
                            defaultMessage: 'Психолог',
                          }),
                          year: [
                            intl.formatMessage({
                              id: 'other.year.one',
                              defaultMessage: 'рік',
                            }),
                            intl.formatMessage({
                              id: 'other.year.many',
                              defaultMessage: 'роки',
                            }),
                            intl.formatMessage({
                              id: 'other.year.several',
                              defaultMessage: 'років',
                            }),
                          ],
                          experiences: intl.formatMessage({
                            id: 'specialist.card.experiences',
                            defaultMessage: 'досвіду',
                          }),
                          hour: [
                            intl.formatMessage({
                              id: 'other.hour.one',
                              defaultMessage: 'година',
                            }),
                            intl.formatMessage({
                              id: 'other.hour.many',
                              defaultMessage: 'години',
                            }),
                            intl.formatMessage({
                              id: 'other.hour.several',
                              defaultMessage: 'годин',
                            }),
                          ],
                          practice: intl.formatMessage({
                            id: 'specialist.card.practice',
                            defaultMessage: 'практики',
                          }),
                          session: intl.formatMessage({
                            id: 'specialist.card.session',
                            defaultMessage: 'хв. сеанс',
                          }),
                          clients: [
                            intl.formatMessage({
                              id: 'other.clients.one',
                              defaultMessage: 'клієнт',
                            }),
                            intl.formatMessage({
                              id: 'other.clients.many',
                              defaultMessage: 'клієнти',
                            }),
                            intl.formatMessage({
                              id: 'other.clients.several',
                              defaultMessage: 'клієнтів',
                            }),
                          ],
                          sessions: [
                            intl.formatMessage({
                              id: 'other.sessions.one',
                              defaultMessage: 'сеанс',
                            }),
                            intl.formatMessage({
                              id: 'other.sessions.many',
                              defaultMessage: 'сеанси',
                            }),
                            intl.formatMessage({
                              id: 'other.sessions.several',
                              defaultMessage: 'сеансів',
                            }),
                          ],
                          by: intl.formatMessage({
                            id: 'other.by',
                            defaultMessage: 'за',
                          }),
                          match: intl.formatMessage({
                            id: 'specialist.card.match',
                            defaultMessage: 'підходить Вам на',
                          }),
                          reviews: [
                            intl.formatMessage({
                              id: 'other.specialistReviews.one',
                              defaultMessage: 'відгук',
                            }),
                            intl.formatMessage({
                              id: 'other.specialistReviews.many',
                              defaultMessage: 'відгуки',
                            }),
                            intl.formatMessage({
                              id: 'other.specialistReviews.several',
                              defaultMessage: 'відгуків',
                            }),
                          ],
                        }}
                      />
                    );
                  }
                })}
              </div>
            </OnBoardingSpecialistsWrapper>
          )}
          <SpecialistProfileVideoModal
            isOpen={!!videoURL}
            videoLink={videoURL ?? ''}
            onDidDismiss={onSpecialistVideoClose}
            mode={'md'}
            closeModalHandler={onSpecialistVideoClose}
          />
        </Container>
      </IonContent>
      <OnBoardingFooter>
        <CustomButton
          style={{ marginTop: '2px' }}
          type="button"
          buttonType="primary"
          onClick={async () => {
            isomorficHapticsImpact({ style: ImpactStyle.Light });
            await handleNextStep();
          }}
          disabled={isLoading}
          loading={isLoading}
        >
          <FormattedMessage id={'on-boarding.specialist.save'} defaultMessage={'Зберегти підбірку психологів'} />
        </CustomButton>
      </OnBoardingFooter>
    </IonPage>
  );
};

export default OnBoardingSpecialists;
